import { detectStudentColumns, getStudentClassInfo, resolveClassName, normalizeYear } from './classNameProcessor'

// src/utils/authorityAnalyzer.js

/**
 * Analyzes staff authority levels and categorizes them
 * @param {Array} staffData - Array of staff data objects
 * @param {Array} studentsData - Optional array of student data objects
 * @returns {Object} - Analysis results and categorized staff
 */
export const analyzeStaffAuthority = (staffData, studentsData = []) => {
    if (!staffData || !staffData.length) {
        return {
            categorized: {},
            summary: {},
            issues: [],
        }
    }

    // Find required columns
    const authorityColumn = findAuthorityColumn(staffData[0])
    const emailColumn = findEmailColumn(staffData[0])
    const firstNameColumn = findFirstNameColumn(staffData[0])
    const lastNameColumn = findLastNameColumn(staffData[0])
    const passwordColumn = findPasswordColumn(staffData[0])

    // Check for required columns
    const requiredColumnIssues = []
    if (!authorityColumn) {
        requiredColumnIssues.push({ type: 'error', message: 'No authority column found in staff data' })
    }
    if (!emailColumn) {
        requiredColumnIssues.push({ type: 'error', message: 'No email column found in staff data. Staff must have email addresses.' })
    }
    if (!firstNameColumn) {
        requiredColumnIssues.push({ type: 'error', message: 'No first name column found in staff data. Staff must have first names.' })
    }
    if (!lastNameColumn) {
        requiredColumnIssues.push({ type: 'error', message: 'No last name column found in staff data. Staff must have last names.' })
    }

    if (requiredColumnIssues.length > 0) {
        return {
            categorized: {},
            summary: {},
            issues: requiredColumnIssues,
            emailColumn,
            firstNameColumn,
            lastNameColumn,
            authorityColumn,
            passwordColumn,
        }
    }

    // Initialize result objects
    const categorized = {
        fullAccess: [],
        headOfKeyStage: [], // NEW: Head of Key Stage category
        headOfYear: [],
        teachers: [],
        blank: [],
        unknown: [],
        missingPasswords: [],
        missingEmails: [],
        missingFirstNames: [],
        missingLastNames: [],
        duplicateEmails: [],
        validRecords: [],
    }

    const issues = []
    const emails = new Map() // Track emails and their indexes for duplicate detection

    // Extract class and year information from student data once
    const { classNames, yearGroups, availableClasses, availableYears, classInfoByName } = extractClassAndYearInfo(studentsData)

    console.log('Class names:', classNames)
    console.log('Year groups:', yearGroups)
    console.log('Available classes:', availableClasses)
    console.log('Available years:', availableYears)

    // Process each staff member
    staffData.forEach((staff, index) => {
        const authority = staff[authorityColumn] ? String(staff[authorityColumn]).trim() : ''
        const email = staff[emailColumn] ? String(staff[emailColumn]).trim().toLowerCase() : ''
        const firstName = staff[firstNameColumn] ? String(staff[firstNameColumn]).trim() : ''
        const lastName = staff[lastNameColumn] ? String(staff[lastNameColumn]).trim() : ''
        const staffName = getStaffName(staff)
        const password = passwordColumn ? staff[passwordColumn] : null
        let hasValidationIssue = false

        // Check for missing mandatory fields
        if (!email) {
            categorized.missingEmails.push({ ...staff, staffIndex: index })
            issues.push({
                type: 'error',
                message: `Staff member ${staffName} (row ${index + 1}) is missing email address`,
                staff,
                index,
            })
            hasValidationIssue = true
        } else {
            // Validate email format
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                issues.push({
                    type: 'warning',
                    message: `Staff member ${staffName} has invalid email format: ${email}`,
                    staff,
                    index,
                })
            }

            // Check for duplicate emails
            if (emails.has(email)) {
                const existingIndex = emails.get(email)
                const existingStaff = staffData[existingIndex]

                categorized.duplicateEmails.push({
                    ...staff,
                    staffIndex: index,
                    duplicateWith: existingIndex,
                })

                if (!issues.some((issue) => issue.type === 'error' && issue.message.includes(`Duplicate email: ${email}`))) {
                    issues.push({
                        type: 'error',
                        message: `Duplicate email: ${email} - Used by ${getStaffName(existingStaff)} (row ${existingIndex + 1}) and ${staffName} (row ${index + 1})`,
                        staff,
                        index,
                        duplicateWith: existingIndex,
                    })
                }
                hasValidationIssue = true
            } else {
                emails.set(email, index)
            }
        }

        if (!firstName) {
            categorized.missingFirstNames.push({ ...staff, staffIndex: index })
            issues.push({
                type: 'error',
                message: `Staff member in row ${index + 1} is missing first name`,
                staff,
                index,
            })
            hasValidationIssue = true
        }

        if (!lastName) {
            categorized.missingLastNames.push({ ...staff, staffIndex: index })
            issues.push({
                type: 'error',
                message: `Staff member in row ${index + 1} is missing last name`,
                staff,
                index,
            })
            hasValidationIssue = true
        }

        // Check for missing passwords if password column exists
        // if (passwordColumn && (!password || String(password).trim() === '')) {
        //     categorized.missingPasswords.push({ ...staff, staffIndex: index })
        //     issues.push({
        //         type: 'warning',
        //         message: `Staff member ${staffName} has no password set. It will be auto-generated.`,
        //         staff,
        //         index,
        //     })
        // }

        // Add to Valid Entries if no validation issues found
        if (!hasValidationIssue) {
            categorized.validRecords.push({ ...staff, staffIndex: index })
        }

        // Initialize scope information
        let scopeField = ''
        let scope = []

        // Categorize based on authority value - CHECK IN ORDER FROM HIGHEST TO LOWEST AUTHORITY
        if (!authority) {
            categorized.blank.push({ ...staff, scopeField: 'none', scope: [], staffIndex: index })
        } else {
            // 1. FIRST: Check if it's full access (HIGHEST AUTHORITY)
            if (isFullAccessAuthority(authority)) {
                scopeField = 'admin'
                scope = ['All Students']
                categorized.fullAccess.push({ ...staff, scopeField, scope, staffIndex: index })
            }
            // 2. SECOND: Check for Head of Key Stage (SECOND HIGHEST AUTHORITY)
            else {
                const hoksInfo = extractHeadOfKeyStageInfo(authority, availableYears)
                if (hoksInfo.isHOKS) {
                    scopeField = 'year'
                    scope = hoksInfo.yearScope
                    categorized.headOfKeyStage.push({
                        ...staff,
                        yearScope: hoksInfo.yearScope,
                        scopeField,
                        scope,
                        staffIndex: index,
                    })
                }
                // 3. THIRD: Check for Head of Year (MEDIUM-HIGH AUTHORITY)
                else {
                    const hoyInfo = extractHeadOfYearInfo(authority, yearGroups, availableYears)
                    if (hoyInfo.isHOY) {
                        scopeField = 'year'
                        scope = hoyInfo.yearsList
                        categorized.headOfYear.push({
                            ...staff,
                            yearResponsibilities: hoyInfo.yearsList,
                            scopeField,
                            scope,
                            staffIndex: index,
                        })
                    }
                    // 4. FOURTH: Check if it's a teacher with specific class assignments (MEDIUM AUTHORITY)
                    else {
                        const teacherInfo = extractTeacherClassInfo(authority, availableClasses, classInfoByName)
                        if (teacherInfo.isTeacher) {
                            scopeField = 'class'
                            scope = teacherInfo.classList
                            categorized.teachers.push({
                                ...staff,
                                scopeField,
                                scope,
                                assignedClassNames: scope,
                                assignedClasses: teacherInfo.assignedClasses,
                                staffIndex: index,
                            })
                            teacherInfo.ambiguous.forEach(({ part, candidates }) => {
                                issues.push({
                                    type: 'warning',
                                    message: `Staff member ${staffName} (row ${index + 1}): class "${part}" exists in several years (${candidates.join(', ')}). Use the full class name, e.g. "${candidates[0]}"`,
                                    staff,
                                    index,
                                })
                            })
                            teacherInfo.unmatched.forEach((part) => {
                                issues.push({
                                    type: 'warning',
                                    message: `Staff member ${staffName} (row ${index + 1}): class "${part}" was not found in the student data`,
                                    staff,
                                    index,
                                })
                            })
                        }
                        // 5. FINALLY: Unknown format (LOWEST)
                        else {
                            scopeField = 'unknown'
                            scope = []
                            categorized.unknown.push({ ...staff, authorityIssue: 'Unrecognized authority format', scopeField, scope, staffIndex: index })
                            issues.push({
                                type: 'warning',
                                message: `Staff member ${staffName} (row ${index + 1}) has unrecognized authority: "${authority}"`,
                                staff,
                                index,
                            })
                        }
                    }
                }
            }
        }
    })

    // Generate summary
    const summary = {
        total: staffData.length,
        fullAccess: categorized.fullAccess.length,
        headOfKeyStage: categorized.headOfKeyStage.length,
        headOfYear: categorized.headOfYear.length,
        teachers: categorized.teachers.length,
        blank: categorized.blank.length,
        unknown: categorized.unknown.length,
        validRecords: categorized.validRecords.length,
        missingEmails: categorized.missingEmails.length,
        missingFirstNames: categorized.missingFirstNames.length,
        missingLastNames: categorized.missingLastNames.length,
        duplicateEmails: categorized.duplicateEmails.length,
        missingPasswords: categorized.missingPasswords.length,
        issuesCount: issues.length,
    }

    return {
        categorized,
        summary,
        issues,
        emailColumn,
        firstNameColumn,
        lastNameColumn,
        authorityColumn,
        passwordColumn,
    }
}

/**
 * NEW: Extract Head of Key Stage information from authority string
 * @param {String} authority - The authority string to check
 * @param {Array} availableYears - Array of formatted available years from student data
 * @returns {Object} - Object with isHOKS boolean, keyStageType, and keyStageScope
 */
const extractHeadOfKeyStageInfo = (authority, availableYears) => {
    if (!authority) return { isHOKS: false, keyStageType: null, yearScope: [] }

    const lowerAuth = authority.toLowerCase().trim()

    // Head of Key Stage patterns
    const hoksPatterns = [
        /head\s+of\s+key\s+stage/i,
        /\bhoks\b/i,
        /key\s+stage\s+head/i,
        /key\s+stage\s+leader/i,
        /key\s+stage\s+coordinator/i,
        /all\s+primary/i,
        /all\s+secondary/i,
        /primary\s+head/i,
        /secondary\s+head/i,
        /primary\s+leader/i,
        /secondary\s+leader/i,
    ]

    // Check if it matches any HOKS pattern
    const matchesHOKSPattern = hoksPatterns.some((pattern) => pattern.test(authority))

    if (!matchesHOKSPattern) {
        return { isHOKS: false, keyStageType: null, yearScope: [] }
    }

    // Determine key stage type and scope
    let keyStageType = null
    let yearScope = []

    // Check for Primary patterns
    if (/all\s+primary|primary\s+(head|leader|coordinator)|head\s+of\s+key\s+stage\s+primary/i.test(lowerAuth)) {
        keyStageType = 'Primary'
        yearScope = getPrimaryYearScope(availableYears)
    }
    // Check for Secondary patterns
    else if (/all\s+secondary|secondary\s+(head|leader|coordinator)|head\s+of\s+key\s+stage\s+secondary/i.test(lowerAuth)) {
        keyStageType = 'Secondary'
        yearScope = getSecondaryYearScope(availableYears)
    }
    // If no specific type mentioned, try to determine from other patterns
    else {
        // Look for key stage numbers (KS1, KS2, etc.)
        const ksMatch = lowerAuth.match(/key\s+stage\s+(\d+)|ks\s*(\d+)/i)
        if (ksMatch) {
            const ksNumber = parseInt(ksMatch[1] || ksMatch[2], 10)
            if (ksNumber <= 2) {
                keyStageType = 'Primary'
                yearScope = getPrimaryYearScope(availableYears)
            } else {
                keyStageType = 'Secondary'
                yearScope = getSecondaryYearScope(availableYears)
            }
        }
        // Default to general Head of Key Stage if no specific type found
        else {
            keyStageType = 'All Key Stages'
            yearScope = [...getPrimaryYearScope(availableYears), ...getSecondaryYearScope(availableYears)]
        }
    }

    return {
        isHOKS: true,
        keyStageType,
        yearScope,
    }
}

/**
 * Get Primary year scope (Years 1-6 and early years)
 * @param {Array} availableYears - Available years from student data
 * @returns {Array} - Array of primary years
 */
const getPrimaryYearScope = (availableYears) => {
    const primaryYears = []
    const primaryPatterns = [
        /^(fs|foundation)\s*\d+/i, // FS1, FS2, Foundation Stage
        /^(prekg|pre-kg|nursery|reception)/i, // Early years
        /^(kg|kindergarten)/i, // Kindergarten
        /^(year|y)\s*[1-6]$/i, // Year 1-6
        /^grade\s*[1-6]$/i, // Grade 1-6
        /^[1-6]$/i, // Just numbers 1-6
    ]

    if (availableYears && availableYears.length > 0) {
        availableYears.forEach((year) => {
            const yearStr = String(year).toLowerCase().trim()
            if (primaryPatterns.some((pattern) => pattern.test(yearStr))) {
                primaryYears.push(year)
            }
        })
    }

    // If no available years, return default primary scope
    if (primaryYears.length === 0) {
        return ['FS1', 'FS2', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6']
    }

    return primaryYears.sort()
}

/**
 * Get Secondary year scope (Years 7-13)
 * @param {Array} availableYears - Available years from student data
 * @returns {Array} - Array of secondary years
 */
const getSecondaryYearScope = (availableYears) => {
    const secondaryYears = []
    const secondaryPatterns = [
        /^(year|y)\s*([7-9]|1[0-3])$/i, // Year 7-13
        /^grade\s*([7-9]|1[0-3])$/i, // Grade 7-13
        /^([7-9]|1[0-3])$/i, // Just numbers 7-13
    ]

    if (availableYears && availableYears.length > 0) {
        availableYears.forEach((year) => {
            const yearStr = String(year).toLowerCase().trim()
            if (secondaryPatterns.some((pattern) => pattern.test(yearStr))) {
                secondaryYears.push(year)
            }
        })
    }

    // If no available years, return default secondary scope
    if (secondaryYears.length === 0) {
        return ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13']
    }

    return secondaryYears.sort()
}

// ... [rest of the existing functions remain the same] ...

/**
 * Finds the column that likely contains authority information
 * @param {Object} staffSample - A sample staff data object
 * @returns {String|null} - The authority column name or null if not found
 */
const findAuthorityColumn = (staffSample) => {
    if (!staffSample) return null

    const possibleAuthorityColumns = ['Authority', 'authority', 'Role', 'role', 'Access', 'access', 'Permission', 'permission', 'Privilege', 'privilege']

    // First try exact matches
    for (const column of possibleAuthorityColumns) {
        if (staffSample.hasOwnProperty(column)) {
            return column
        }
    }

    // Then try partial matches
    const columns = Object.keys(staffSample)
    for (const column of columns) {
        const lowerColumn = column.toLowerCase()
        if (
            lowerColumn.includes('authority') ||
            lowerColumn.includes('role') ||
            lowerColumn.includes('access') ||
            lowerColumn.includes('permission') ||
            lowerColumn.includes('privilege')
        ) {
            return column
        }
    }

    return null
}

/**
 * Finds the column that likely contains password information
 * @param {Object} staffSample - A sample staff data object
 * @returns {String|null} - The password column name or null if not found
 */
const findPasswordColumn = (staffSample) => {
    if (!staffSample) return null

    const possiblePasswordColumns = ['password', 'Password', 'pwd', 'Pwd', 'pass', 'Pass']

    // First try exact matches
    for (const column of possiblePasswordColumns) {
        if (staffSample.hasOwnProperty(column)) {
            return column
        }
    }

    // Then try partial matches
    const columns = Object.keys(staffSample)
    for (const column of columns) {
        const lowerColumn = column.toLowerCase()
        if (lowerColumn.includes('password') || lowerColumn.includes('pwd') || lowerColumn.includes('pass')) {
            return column
        }
    }

    return null
}

/**
 * Finds the column that likely contains email information
 * @param {Object} staffSample - A sample staff data object
 * @returns {String|null} - The email column name or null if not found
 */
const findEmailColumn = (staffSample) => {
    if (!staffSample) return null

    const possibleEmailColumns = ['email', 'Email', 'EMAIL', 'e-mail', 'E-mail', 'username', 'Username', 'user name', 'User Name', 'mail', 'Mail']

    // First try exact matches
    for (const column of possibleEmailColumns) {
        if (staffSample.hasOwnProperty(column)) {
            return column
        }
    }

    // Then try partial matches
    const columns = Object.keys(staffSample)
    for (const column of columns) {
        const lowerColumn = column.toLowerCase()
        if (lowerColumn.includes('email') || lowerColumn.includes('mail') || (lowerColumn.includes('user') && lowerColumn.includes('name'))) {
            return column
        }
    }

    return null
}

/**
 * Finds the column that likely contains first name information
 * @param {Object} staffSample - A sample staff data object
 * @returns {String|null} - The first name column name or null if not found
 */
const findFirstNameColumn = (staffSample) => {
    if (!staffSample) return null

    const possibleFirstNameColumns = ['first name', 'First Name', 'firstname', 'FirstName', 'FIRST NAME', 'fname', 'Fname', 'given name', 'Given Name']

    // First try exact matches
    for (const column of possibleFirstNameColumns) {
        if (staffSample.hasOwnProperty(column)) {
            return column
        }
    }

    // Then try partial matches
    const columns = Object.keys(staffSample)
    for (const column of columns) {
        const lowerColumn = column.toLowerCase()
        if (
            (lowerColumn.includes('first') && lowerColumn.includes('name')) ||
            lowerColumn === 'firstname' ||
            lowerColumn === 'fname' ||
            (lowerColumn.includes('given') && lowerColumn.includes('name'))
        ) {
            return column
        }
    }

    return null
}

/**
 * Finds the column that likely contains last name information
 * @param {Object} staffSample - A sample staff data object
 * @returns {String|null} - The last name column name or null if not found
 */
const findLastNameColumn = (staffSample) => {
    if (!staffSample) return null

    const possibleLastNameColumns = ['last name', 'Last Name', 'lastname', 'LastName', 'LAST NAME', 'lname', 'Lname', 'surname', 'Surname', 'family name', 'Family Name']

    // First try exact matches
    for (const column of possibleLastNameColumns) {
        if (staffSample.hasOwnProperty(column)) {
            return column
        }
    }

    // Then try partial matches
    const columns = Object.keys(staffSample)
    for (const column of columns) {
        const lowerColumn = column.toLowerCase()
        if (
            (lowerColumn.includes('last') && lowerColumn.includes('name')) ||
            lowerColumn === 'lastname' ||
            lowerColumn === 'lname' ||
            lowerColumn === 'surname' ||
            (lowerColumn.includes('family') && lowerColumn.includes('name'))
        ) {
            return column
        }
    }

    return null
}

/**
 * Extract class names, year groups, and class patterns from student data
 * @param {Array} studentsData - Array of student data objects
 * @returns {Object} - Object containing class names, year groups, and available classes/years as arrays
 */
const extractClassAndYearInfo = (studentsData) => {
    const classNames = new Set()
    const yearGroups = new Set()
    const availableClasses = new Set()
    const availableYears = new Set()
    const yearVariations = new Map() // Track year variations

    if (!studentsData || studentsData.length === 0) {
        return {
            classNames: [],
            yearGroups: [],
            availableClasses: [],
            availableYears: [],
            yearVariations: new Map(),
            classInfoByName: new Map(),
        }
    }

    // Find class and year columns (shared detection with the upload processor)
    const columns = detectStudentColumns(studentsData)
    const { yearColumn } = columns

    // Resolved class name -> { years: Set<string>, rawNames: Set<string> } so that a teacher authority
    // such as "1" can be resolved to "YR-1-1" (and flagged when it exists in several years)
    const classInfoByName = new Map()

    // Process student data to extract class names and year groups
    studentsData.forEach((student) => {
        // Extract class names
        const classInfo = getStudentClassInfo(student, columns)
        if (classInfo.className) {
            const className = classInfo.className
            classNames.add(className.toLowerCase())
            availableClasses.add(className) // Keep original case for display

            const entry = classInfoByName.get(className) || { years: new Set(), rawNames: new Set() }
            if (classInfo.year) entry.years.add(classInfo.year)
            if (classInfo.rawClassName) entry.rawNames.add(classInfo.rawClassName)
            classInfoByName.set(className, entry)

            // Extract year information from class name using improved pattern matching
            const yearInfo = extractYearFromClassName(className)
            if (yearInfo.year !== null) {
                const yearKey = yearInfo.year.toString()
                yearGroups.add(yearKey)
                availableYears.add(formatYearForDisplay(yearKey))

                // Store variations for this year
                const variations = generateYearVariations(yearKey)
                variations.forEach((variation) => {
                    yearVariations.set(variation, formatYearForDisplay(yearKey))
                })
            }

            // Extract special prefixes with improved handling
            const specialYear = extractSpecialYearInfo(className)
            if (specialYear) {
                yearGroups.add(specialYear.code)
                availableYears.add(specialYear.display)

                // Store variations for special years
                const variations = generateYearVariations(specialYear.code)
                variations.forEach((variation) => {
                    yearVariations.set(variation, specialYear.display)
                })
            }
        }

        // Extract year groups from year column
        if (yearColumn && student[yearColumn]) {
            const year = student[yearColumn].toString().trim()
            const normalizedYear = normalizeYearFormat(year)
            yearGroups.add(normalizedYear)
            const displayYear = formatYearForDisplay(year)
            availableYears.add(displayYear)

            // Store variations
            const variations = generateYearVariations(year)
            variations.forEach((variation) => {
                yearVariations.set(variation, displayYear)
            })
        }
    })

    // Convert Sets to sorted arrays
    return {
        classNames: Array.from(classNames).sort(),
        yearGroups: Array.from(yearGroups).sort(),
        availableClasses: Array.from(availableClasses).sort(),
        availableYears: Array.from(availableYears).sort(),
        yearVariations,
        classInfoByName,
    }
}

// ... [include all the other existing functions like extractYearFromClassName, extractSpecialYearInfo, etc.] ...

/**
 * Extract year information from class name with better pattern matching
 * @param {String} className - Class name to analyze
 * @returns {Object} - Object with year and section information
 */
const extractYearFromClassName = (className) => {
    if (!className) return { year: null, section: null }

    const patterns = [/grade\s*(\d+)\s*([a-z]?)/i, /year\s*(\d+)\s*([a-z]?)/i, /class\s*(\d+)\s*([a-z]?)/i, /^(\d+)\s*([a-z]?)$/i]

    for (const pattern of patterns) {
        const match = className.match(pattern)
        if (match) {
            return {
                year: parseInt(match[1], 10),
                section: match[2] || null,
            }
        }
    }

    return { year: null, section: null }
}

/**
 * Extract special year information (FS, KG, etc.) from class name
 * @param {String} className - Class name to analyze
 * @returns {Object|null} - Object with code and display or null
 */
const extractSpecialYearInfo = (className) => {
    if (!className) return null

    const lowerClassName = className.toLowerCase()

    if (/\bfs\s*\d+/i.test(className)) {
        const fsMatch = className.match(/\bfs\s*(\d+)/i)
        if (fsMatch) {
            return {
                code: `fs${fsMatch[1]}`,
                display: `FS${fsMatch[1]}`,
            }
        }
    }

    if (/\bpre-?kg\b/i.test(lowerClassName)) {
        return {
            code: 'prekg',
            display: 'Pre-KG',
        }
    }

    if (/\bkg\b/i.test(lowerClassName)) {
        return {
            code: 'kg',
            display: 'KG',
        }
    }

    if (/\bnursery\b/i.test(lowerClassName)) {
        return {
            code: 'nursery',
            display: 'Nursery',
        }
    }

    if (/\breception\b/i.test(lowerClassName)) {
        return {
            code: 'reception',
            display: 'Reception',
        }
    }

    return null
}

const stripSpaces = (str) =>
    String(str ?? '')
        .replace(/\s+/g, '')
        .toLowerCase()

/**
 * Extract the grade/year number and optional section letter from strings like
 * "grade 4 a", "year 3 b", "class 7", "4a", "fs 1 b"
 * @param {String} str
 * @returns {{ number: String, letter: String }|null}
 */
const extractGradePattern = (str) => {
    const patterns = [/grade\s*(\d+)\s*([a-z]?)/i, /year\s*(\d+)\s*([a-z]?)/i, /class\s*(\d+)\s*([a-z]?)/i, /^(\d+)\s*([a-z]?)$/i, /fs\s*(\d+)\s*([a-z]?)/i]

    for (const pattern of patterns) {
        const match = String(str ?? '').match(pattern)
        if (match) {
            return { number: String(Number.parseInt(match[1], 10)), letter: (match[2] || '').toLowerCase() }
        }
    }
    return null
}

/**
 * Match one comma-separated authority part against the classes found in the student data.
 *  - exact: the part names the class unambiguously (same name, "Grade 4 A" vs "4A", "1A" vs year 1 + "A",
 *           or a "year class" pair such as "YR-1 1")
 *  - derived: the part is the raw class value and resolves to the class through one of its years
 *             ("1" -> "YR-1-1"); several hits mean the same class value exists in several years
 * @param {String} part
 * @param {Array<String>} availableClasses
 * @param {Map} classInfoByName
 * @returns {{ exact: Array<String>, derived: Array<String> }}
 */
const matchAuthorityPart = (part, availableClasses = [], classInfoByName = new Map()) => {
    const exact = []
    const derived = []
    const partLower = String(part).toLowerCase().trim()
    const yearClassPair = part.match(/^(.+?)[\s/]+(\S+)$/)

    for (const availableClass of availableClasses) {
        const info = classInfoByName.get(availableClass) || { years: new Set(), rawNames: new Set() }
        const availableStripped = stripSpaces(availableClass)

        if (isClassMatch(partLower, availableClass.toLowerCase(), info)) {
            exact.push(availableClass)
            continue
        }

        if (yearClassPair && stripSpaces(resolveClassName(yearClassPair[1], yearClassPair[2])) === availableStripped) {
            exact.push(availableClass)
            continue
        }

        const resolvesThroughYear = Array.from(info.years).some((year) => stripSpaces(resolveClassName(year, part)) === availableStripped)
        if (resolvesThroughYear) {
            derived.push(availableClass)
        }
    }

    return { exact, derived }
}

/**
 * Extract teacher class information from authority string
 * @param {String} authority - The authority string to analyze
 * @param {Array} availableClasses - Array of available (resolved) class names from student data
 * @param {Map} classInfoByName - Resolved class name -> { years, rawNames } (from extractClassAndYearInfo)
 * @returns {Object} - { isTeacher, classList, assignedClasses, ambiguous, unmatched }
 */
const extractTeacherClassInfo = (authority, availableClasses = [], classInfoByName = new Map()) => {
    const result = { isTeacher: false, classList: [], assignedClasses: [], ambiguous: [], unmatched: [] }
    if (!authority) return result

    const lowerAuth = authority.toLowerCase().trim()

    // Check for explicit teacher designation
    if (lowerAuth === 'teacher' || lowerAuth.includes('class teacher') || lowerAuth.includes('subject teacher')) {
        return { ...result, isTeacher: true, classList: ['General Teaching'] }
    }

    const addClass = (name) => {
        if (result.classList.includes(name)) return
        result.classList.push(name)
        const years = Array.from(classInfoByName.get(name)?.years ?? [])
        result.assignedClasses.push({ name, year: years.length === 1 ? years[0] : null })
    }

    // Split by commas to handle multiple classes
    const authParts = authority
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)

    for (const part of authParts) {
        const { exact, derived } = matchAuthorityPart(part, availableClasses, classInfoByName)

        if (exact.length > 0) {
            result.isTeacher = true
            exact.forEach(addClass)
        } else if (derived.length === 1) {
            result.isTeacher = true
            addClass(derived[0])
        } else if (derived.length > 1) {
            result.isTeacher = true
            result.ambiguous.push({ part, candidates: derived })
        } else if (isValidClassPattern(part)) {
            // Looks like a class but is not in the student data: keep it so the backend can try, and warn
            result.isTeacher = true
            addClass(part)
            result.unmatched.push(part)
        }
    }

    return result
}

/**
 * Check if an authority class name and an available class name denote the same class.
 * Handles "Grade 4 A" vs "4A" and "1A" vs a class built from year "YR-1" + section "A".
 * Deliberately no substring matching: "1" must not match "10A".
 * @param {String} auth - Authority class name (lowercase)
 * @param {String} available - Available class name (lowercase)
 * @param {{ years: Set<String>, rawNames: Set<String> }} [info] - Year / raw value info of the available class
 * @returns {Boolean} - True if they match
 */
const isClassMatch = (auth, available, info) => {
    // Direct match
    if (auth === available) return true

    // Normalized match (whitespace-insensitive)
    if (stripSpaces(auth) === stripSpaces(available)) return true

    const authPattern = extractGradePattern(auth)
    if (!authPattern) return false

    // Check for grade/year pattern matches ("grade 4 a" vs "4a")
    const availablePattern = extractGradePattern(available)
    if (availablePattern && authPattern.number === availablePattern.number && authPattern.letter === availablePattern.letter) {
        return true
    }

    // "1A" vs a class resolved from year "YR-1" + section "A"
    if (info && authPattern.letter) {
        const yearMatches = Array.from(info.years).some((year) => normalizeYear(year) === authPattern.number)
        if (yearMatches) {
            return Array.from(info.rawNames).some((rawName) => /^[a-z]$/i.test(rawName) && rawName.toLowerCase() === authPattern.letter)
        }
    }

    return false
}

/**
 * Check if a string matches valid class patterns
 * @param {String} str - String to check
 * @returns {Boolean} - True if it's a valid class pattern
 */
const isValidClassPattern = (str) => {
    const patterns = [
        /^\d+[a-z]?$/i, // 1A, 2B, etc.
        /^fs\s*\d+\s*[a-z]?$/i, // FS1A, FS 2, etc.
        /^(kg|prekg|nursery|reception)$/i,
        /^year\s*\d+\s*[a-z]?$/i, // Year 1A, Year 2 B, etc.
        /^grade\s*\d+\s*[a-z]?$/i, // Grade 1A, Grade 2 B, etc.
        /^class\s*\d+\s*[a-z]?$/i, // Class 1A, Class 2 B, etc.
    ]

    return patterns.some((pattern) => pattern.test(str.trim()))
}

/**
 * Determines if the authority string indicates full access
 * @param {String} authority - The authority string to check
 * @returns {Boolean} - True if it's a full access authority
 */
const isFullAccessAuthority = (authority) => {
    if (!authority) return false

    const lowerAuth = authority.toLowerCase()

    // Check for full access patterns
    return (
        lowerAuth.includes('admin') ||
        lowerAuth.includes('counsellor') ||
        lowerAuth.includes('counselor') ||
        lowerAuth.includes('all access') ||
        lowerAuth.includes('full access') ||
        lowerAuth.includes('principal') ||
        lowerAuth.includes('head teacher') ||
        lowerAuth.includes('headteacher') ||
        lowerAuth.includes('head master') ||
        lowerAuth.includes('headmaster') ||
        lowerAuth.includes('director') ||
        lowerAuth.includes('superintendent') ||
        lowerAuth.includes('all students') ||
        lowerAuth.includes('all students and staff') ||
        lowerAuth.includes('slt') ||
        lowerAuth.includes('all classes and years')
    )
}

/**
 * Determines if the authority string indicates a Head of Year role and extracts the specific years
 * @param {String} authority - The authority string to check
 * @param {Array} yearGroups - Array of known year groups from student data
 * @param {Array} availableYears - Array of formatted available years from student data
 * @returns {Object} - Object with isHOY boolean and yearsList array of specific years
 */
const extractHeadOfYearInfo = (authority, yearGroups, availableYears, yearVariations = new Map()) => {
    if (!authority) return { isHOY: false, yearsList: [] }

    const lowerAuth = authority.toLowerCase()
    let yearsList = []
    let isHOY = false

    // ENHANCED HOY patterns - including ALL FS patterns
    const strictHOYPatterns = [
        /head\s+of\s+year/i,
        /head\s+of\s+grade/i,
        /\bhoy\b/i,
        /year\s+leader/i,
        /year\s+head/i,
        /grade\s+leader/i,
        /year\s+coordinator/i,
        /\byear\s*\d+\s*lead(er)?\b/i,
        /\ball\s+y/i, // "ALL Y01", "ALL Y1", etc.
        /\ball\s+fs/i, // "ALL FS1", "ALL FS2", etc. - NEW PATTERN
        /\ball\s+grade/i, // "ALL GRADE 1", etc.
        /\ball\s+year/i, // "ALL YEAR 1", etc.
    ]

    // Check if it matches any strict HOY pattern
    const matchesHOYPattern = strictHOYPatterns.some((pattern) => pattern.test(authority))

    if (!matchesHOYPattern) {
        return { isHOY: false, yearsList: [] }
    }

    // If we get here, it matches a HOY pattern, so extract the years
    isHOY = true

    // ENHANCED year extraction patterns - including FS patterns
    const yearPatterns = [
        /\b(year|grade)\s*0*(\d+)\b/gi,
        /\b(y|yr)\s*0*(\d+)\b/gi,
        /\bfs\s*0*(\d+)\b/gi, // FS1, FS2, etc.
        /\ball\s+(y|yr)\s*0*(\d+)(?:\s+and\s+(y|yr)\s*0*(\d+))*/gi,
        /\ball\s+fs\s*0*(\d+)(?:\s+and\s+fs\s*0*(\d+))*/gi, // NEW: "ALL FS1", "ALL FS1 AND FS2"
        /\ball\s+grade\s*0*(\d+)(?:\s+and\s+grade\s*0*(\d+))*/gi, // "ALL GRADE 1"
        /\ball\s+year\s*0*(\d+)(?:\s+and\s+year\s*0*(\d+))*/gi, // "ALL YEAR 1"
        /head\s+of\s+year\s*([0-9]+(?:\s?,\s{0,2}[0-9]+)*)/gi, // "Head of Year 1,2,3"
    ]

    // Process each pattern
    for (const pattern of yearPatterns) {
        let match
        while ((match = pattern.exec(authority)) !== null) {
            // Handle different pattern groups
            if (match[0].toLowerCase().includes('fs')) {
                // FS pattern - extract FS numbers
                const fsMatch = match[0].match(/fs\s*0*(\d+)/gi)
                if (fsMatch) {
                    fsMatch.forEach((fs) => {
                        const fsNum = fs.match(/\d+/)[0]

                        const matchingYear = findBestYearMatch(`fs${fsNum}`, availableYears, yearVariations)
                        if (matchingYear && !yearsList.includes(matchingYear)) {
                            yearsList.push(matchingYear)
                        }
                    })
                }
            } else if (match[2]) {
                // Regular year pattern
                const yearNum = parseInt(match[2], 10)
                const matchingYear = findBestYearMatch(yearNum.toString(), availableYears, yearVariations)
                if (matchingYear && !yearsList.includes(matchingYear)) {
                    yearsList.push(matchingYear)
                }
            }

            // Handle "and" patterns for additional years
            if (match[4]) {
                // Second year in "and" pattern
                const yearNum = parseInt(match[4], 10)
                const matchingYear = findBestYearMatch(yearNum.toString(), availableYears, yearVariations)
                if (matchingYear && !yearsList.includes(matchingYear)) {
                    yearsList.push(matchingYear)
                }
            }
            if (match[1] && /([0-9]+(?:\s*,\s*[0-9]+)+)/.test(match[1])) {
                const yearNums = match[1].split(',').map((y) => y.trim())
                yearNums.forEach((yearNum) => {
                    const matchingYear = findBestYearMatch(yearNum, availableYears, yearVariations)
                    if (matchingYear && !yearsList.includes(matchingYear)) {
                        yearsList.push(matchingYear)
                    }
                })
            }
        }
    }

    // Handle range patterns like "Y1-Y3" or "Y01-Y03" or "FS1-FS2"
    const rangePatterns = [
        /\b(y|yr)\s*0*(\d+)\s*[-–]\s*(y|yr)?\s*0*(\d+)\b/gi,
        /\bfs\s*0*(\d+)\s*[-–]\s*(fs)?\s*0*(\d+)\b/gi, // NEW: FS range pattern
        /\bgrade\s*0*(\d+)\s*[-–]\s*(grade)?\s*0*(\d+)\b/gi,
        /\byear\s*0*(\d+)\s*[-–]\s*(year)?\s*0*(\d+)\b/gi,
    ]

    for (const rangePattern of rangePatterns) {
        let rangeMatch
        while ((rangeMatch = rangePattern.exec(authority)) !== null) {
            if (rangeMatch[0].toLowerCase().includes('fs')) {
                // FS range handling
                const startFs = parseInt(rangeMatch[1], 10)
                const endFs = parseInt(rangeMatch[3], 10)

                for (let fs = startFs; fs <= endFs; fs++) {
                    const matchingYear = findBestYearMatch(`fs${fs}`, availableYears, yearVariations)
                    if (matchingYear && !yearsList.includes(matchingYear)) {
                        yearsList.push(matchingYear)
                    }
                }
            } else {
                // Regular year range handling
                const startYear = parseInt(rangeMatch[2], 10)
                const endYear = parseInt(rangeMatch[4], 10)

                for (let year = startYear; year <= endYear; year++) {
                    const matchingYear = findBestYearMatch(year.toString(), availableYears, yearVariations)
                    if (matchingYear && !yearsList.includes(matchingYear)) {
                        yearsList.push(matchingYear)
                    }
                }
            }
        }
    }

    // Check if authority mentions any of the available year groups directly
    if (yearGroups && yearGroups.length > 0) {
        for (const yearGroup of yearGroups) {
            const variations = generateYearVariations(yearGroup)
            for (const variation of variations) {
                if (lowerAuth.includes(variation.toLowerCase())) {
                    const matchingYear = findBestYearMatch(yearGroup, availableYears, yearVariations)
                    if (matchingYear && !yearsList.includes(matchingYear)) {
                        yearsList.push(matchingYear)
                    }
                    break
                }
            }
        }
    }

    // If we determined this is a HOY but couldn't extract specific years,
    // this might be a general HOY role
    if (isHOY && yearsList.length === 0) {
        yearsList = yearGroups
    }

    return { isHOY, yearsList }
}

/**
 * Find the best matching year considering variations and available years
 * @param {String} searchYear - Year to search for
 * @param {Array} availableYears - Available years array
 * @param {Map} yearVariations - Map of variations to display formats
 * @returns {String} - Best matching year
 */
const findBestYearMatch = (searchYear, availableYears, yearVariations) => {
    // First check if we have this exact variation mapped
    const variations = generateYearVariations(searchYear)
    for (const variation of variations) {
        if (yearVariations.has(variation)) {
            return yearVariations.get(variation)
        }
    }

    // Fall back to the original findMatchingYear function
    return findMatchingYear(searchYear, availableYears)
}

/**
 * Gets a display name for a staff member from available fields
 * @param {Object} staff - The staff data object
 * @returns {String} - A display name for the staff member
 */
const getStaffName = (staff) => {
    // Try different name field combinations
    if (staff['First Name'] && staff['Last Name']) {
        return `${staff['First Name']} ${staff['Last Name']}`
    }

    if (staff['FirstName'] && staff['LastName']) {
        return `${staff['FirstName']} ${staff['LastName']}`
    }

    if (staff['Name']) {
        return staff['Name']
    }

    if (staff['Full Name']) {
        return staff['Full Name']
    }

    // Try to find any field that might contain a name
    for (const key of Object.keys(staff)) {
        if (key.toLowerCase().includes('name') && !key.toLowerCase().includes('user') && staff[key]) {
            return staff[key]
        }
    }

    return 'Unknown Staff'
}

/**
 * Normalize year format for consistent comparison
 * @param {String} year - Year string to normalize
 * @returns {String} - Normalized year format
 */
const normalizeYearFormat = (year) => {
    if (!year) return ''

    const yearStr = String(year).toLowerCase().trim()

    // Handle Y01, Y1, Year 1, etc.
    const patterns = [
        { regex: /^y0*(\d+)$/, format: 'y' }, // Y01 -> Y1, Y001 -> Y1
        { regex: /^yr0*(\d+)$/, format: 'yr' }, // YR01 -> YR1
        { regex: /^year\s*0*(\d+)$/, format: 'year' }, // Year 01 -> Year 1
        { regex: /^grade\s*0*(\d+)$/, format: 'grade' }, // Grade 01 -> Grade 1
        { regex: /^fs0*(\d+)$/, format: 'fs' }, // FS01 -> FS1
    ]

    for (const pattern of patterns) {
        const match = yearStr.match(pattern.regex)
        if (match) {
            const number = parseInt(match[1], 10) // Remove leading zeros
            return `${pattern.format}${number.toString().padStart(2, '0')}`
        }
    }

    // Handle plain numbers - pad with leading zeros to 2 digits
    const numberMatch = yearStr.match(/^0*(\d+)$/)
    if (numberMatch) {
        const number = parseInt(numberMatch[1], 10)
        return number.toString().padStart(2, '0')
    }

    return yearStr
}

/**
 * Generate all possible year format variations for matching
 * @param {String} year - Base year string
 * @returns {Array} - Array of possible year formats
 */
const generateYearVariations = (year) => {
    if (!year) return []

    const variations = new Set()
    const yearStr = String(year).toLowerCase().trim()

    // Add the original
    variations.add(yearStr)

    // Handle FS patterns specifically
    const fsMatch = yearStr.match(/fs(\d+)/)
    if (fsMatch) {
        const number = parseInt(fsMatch[1], 10)
        const paddedNumber = number.toString().padStart(2, '0')

        // Generate FS variations
        variations.add(`fs${number}`)
        variations.add(`fs${paddedNumber}`)
        variations.add(`fs ${number}`)
        variations.add(`fs ${paddedNumber}`)

        return Array.from(variations)
    }

    // Extract number if present
    const numberMatch = yearStr.match(/(\d+)/)
    if (numberMatch) {
        const number = parseInt(numberMatch[1], 10)
        const paddedNumber = number.toString().padStart(2, '0') // Add leading zero

        // Generate common variations
        variations.add(`y${number}`)
        variations.add(`y${paddedNumber}`)
        variations.add(`yr${number}`)
        variations.add(`yr${paddedNumber}`)
        variations.add(`year${number}`)
        variations.add(`year ${number}`)
        variations.add(`grade${number}`)
        variations.add(`grade ${number}`)
        variations.add(number.toString())
        variations.add(paddedNumber)

        // Handle FS specifically
        if (yearStr.includes('fs')) {
            variations.add(`fs${number}`)
            variations.add(`fs${paddedNumber}`)
            variations.add(`fs ${number}`)
        }
    }

    return Array.from(variations)
}

/**
 * Check if two year formats match (handling variations like Y1 vs Y01)
 * @param {String} year1 - First year format
 * @param {String} year2 - Second year format
 * @returns {Boolean} - True if they match
 */
const yearFormatsMatch = (year1, year2) => {
    if (!year1 || !year2) return false

    // Normalize both formats
    const normalized1 = normalizeYearFormat(year1)
    const normalized2 = normalizeYearFormat(year2)

    if (normalized1 === normalized2) return true

    // Generate variations and check for matches
    const variations1 = generateYearVariations(year1)
    const variations2 = generateYearVariations(year2)

    // Check if any variation of year1 matches any variation of year2
    for (const v1 of variations1) {
        for (const v2 of variations2) {
            if (v1 === v2) return true
        }
    }

    return false
}

/**
 * Find matching year from available years using flexible matching
 * @param {String} searchYear - The year to search for
 * @param {Array} availableYears - Array of available years
 * @returns {String|null} - Best matching year or formatted version
 */
const findMatchingYear = (searchYear, availableYears) => {
    if (!availableYears || availableYears.length === 0) {
        return formatYearForDisplay(searchYear)
    }

    const searchLower = searchYear.toLowerCase().trim()

    // Try exact match first
    for (const year of availableYears) {
        if (year.toLowerCase() === searchLower) {
            return year
        }
    }

    // Try year format matching
    for (const year of availableYears) {
        if (yearFormatsMatch(searchYear, year)) {
            return year
        }
    }

    // Try partial matches
    for (const year of availableYears) {
        const yearLower = year.toLowerCase()
        if (yearLower.includes(searchLower) || searchLower.includes(yearLower)) {
            return year
        }
    }

    // If no match found, return a nicely formatted version
    return formatYearForDisplay(searchYear)
}

/**
 * Format year for consistent display
 * @param {String} year - Year to format
 * @returns {String} - Formatted year
 */
const formatYearForDisplay = (year) => {
    if (!year) return ''

    const yearStr = String(year).trim()
    // const normalized = normalizeYearFormat(yearStr);

    // // Convert to display format
    // if (normalized.startsWith('y')) {
    // 	const number = normalized.substring(1);
    // 	return `Y${number}`;
    // }
    // if (normalized.startsWith('yr')) {
    // 	const number = normalized.substring(2);
    // 	return `Year ${number}`;
    // }
    // if (normalized.startsWith('year')) {
    // 	const number = normalized.substring(4);
    // 	return `Year ${number}`;
    // }
    // if (normalized.startsWith('grade')) {
    // 	const number = normalized.substring(5);
    // 	return `Grade ${number}`;
    // }
    // if (normalized.startsWith('fs')) {
    // 	const number = normalized.substring(2);
    // 	return `FS${number}`;
    // }

    // // If it's just a number, format as Year X
    // if (/^\d+$/.test(normalized)) {
    // 	return `Year ${normalized}`;
    // }
    return yearStr
    // Capitalize first letter for other formats
    // return yearStr.charAt(0).toUpperCase() + yearStr.slice(1);
}
